/**
 * ResultsSortHeader Component
 *
 * Displays the sort controls and product count for both mobile and desktop views.
 * - Mobile: Product count + Sort dropdown (hidden on desktop)
 * - Desktop: "Showing X Products" + Sort dropdown (hidden on mobile)
 */

import React from "react";

import { type SortOrder } from "@/components/results/sort-utils";
import { SortByButton } from "@/components/results/SortByButton";
import { SortDropdownContent } from "@/components/results/SortDropdownContent";

const SORT_OPTIONS: ReadonlyArray<{ value: SortOrder; label: string }> = [
  { value: "recommended", label: "Recommended" },
  { value: "priceLowToHigh", label: "Price (Low to High)" },
  { value: "priceHighToLow", label: "Price (High to Low)" },
] as const;

export interface ResultsSortHeaderProps {
  /** Number of products to display */
  productCount: number;
  /** Current sort order */
  sortOrder: SortOrder;
  /** Whether the sort menu is open */
  isSortMenuOpen: boolean;
  /** Callback to toggle the sort menu */
  onToggleSortMenu: () => void;
  /** Callback when a sort option is selected */
  onSortSelect: (sort: SortOrder) => void;
  /** Unique ID for the dropdown (accessibility) */
  sortDropdownListId: string;
  /** Which variant to render */
  variant: "mobile" | "desktop";
}

/**
 * Sort header with product count and sort dropdown
 */
export function ResultsSortHeader({
  productCount,
  sortOrder,
  isSortMenuOpen,
  onToggleSortMenu,
  onSortSelect,
  sortDropdownListId,
  variant,
}: ResultsSortHeaderProps): React.ReactElement {
  const sortLabel =
    SORT_OPTIONS.find((option) => option.value === sortOrder)?.label ?? SORT_OPTIONS[0].label;

  if (variant === "mobile") {
    return (
      <div className="mb-4 flex items-center justify-between lg:hidden">
        {productCount > 0 && (
          <p className="text-tag-caps text-ink m-0">
            {productCount} Product{productCount !== 1 ? "s" : ""}
          </p>
        )}
        <div data-sort-control className="relative" style={{ width: "240px" }}>
          <SortByButton
            currentSort={sortLabel}
            onClick={onToggleSortMenu}
            isOpen={isSortMenuOpen}
            dropdownId={sortDropdownListId}
          />
          {isSortMenuOpen && (
            <SortDropdownContent
              activeValue={sortOrder}
              options={SORT_OPTIONS}
              onSelect={onSortSelect}
              dropdownId={sortDropdownListId}
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                right: 0,
                width: "100%",
                zIndex: 200,
              }}
            />
          )}
        </div>
      </div>
    );
  }

  // Desktop variant
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
      }}
    >
      {productCount > 0 && (
        <p className="text-tag-caps text-ink m-0 whitespace-nowrap">
          Showing {productCount} Product
          {productCount !== 1 ? "s" : ""}
        </p>
      )}
      <div data-sort-control className="relative" style={{ width: "240px" }}>
        <SortByButton
          currentSort={sortLabel}
          onClick={onToggleSortMenu}
          isOpen={isSortMenuOpen}
          dropdownId={sortDropdownListId}
        />
        {isSortMenuOpen && (
          <SortDropdownContent
            activeValue={sortOrder}
            options={SORT_OPTIONS}
            onSelect={onSortSelect}
            dropdownId={sortDropdownListId}
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              width: "100%",
              zIndex: 200,
            }}
          />
        )}
      </div>
    </div>
  );
}
